import { useLocalStorage } from '@uidotdev/usehooks';
import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Session from 'supertokens-auth-react/recipe/session';

import { useNamedLogger } from '@eluve/logger';

const MILLISECONDS_PER_MINUTE = 60 * 1000;
const DEFAULT_LOGIN_LOOP_THRESHOLD_MINUTES = 3;

const getLoginMethod = (
  url: string,
): 'google-sso' | 'microsoft-sso' | 'saml-sso' => {
  if (url.includes('google')) return 'google-sso';
  if (url.includes('microsoft')) return 'microsoft-sso';
  return 'saml-sso';
};

export const useSessionPolling = (skip = false) => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const logger = useNamedLogger('useSessionPolling');

  useEffect(() => {
    if (skip) {
      logger.debug('Session polling skipped');
      return;
    }

    const checkSession = async () => {
      try {
        const sessionExists = await Session.doesSessionExist();
        if (sessionExists) {
          logger.info('Session detected, redirecting to dashboard');
          const redirectTo = search.get('redirectTo') ?? '/';
          navigate(redirectTo);
        }
      } catch (error) {
        logger.debug('Error checking session', { error });
      }
    };

    checkSession();
    const interval = setInterval(checkSession, 1000);
    return () => clearInterval(interval);
  }, [navigate, search, logger, skip]);
};

export const useLoginLoopDetection = () => {
  const logger = useNamedLogger('useLoginLoopDetection');
  const [lastLoginAttempt, setLastLoginAttempt] = useLocalStorage<{
    timestamp: string;
    email: string;
  } | null>('eluve-login-loop-detection', null);

  const clearCacheAndRedirect = useCallback(
    async (url: string, userEmail: string, nowIso: string) => {
      try {
        logger.info('Clearing cache due to login loop detection', {
          userEmail,
        });
        setLastLoginAttempt({ timestamp: nowIso, email: userEmail });
        sessionStorage.clear();
        await Session.signOut();
        window.location.replace(url);
      } catch (error) {
        logger.error('Failed to clear cache, falling back to normal redirect', {
          error,
          userEmail,
        });
        window.location.assign(url);
      }
    },
    [logger, setLastLoginAttempt],
  );

  const redirectWithLogging = useCallback(
    (
      url: string,
      options: { userEmail?: string; minutesDiff?: number } = {},
    ) => {
      const { userEmail, minutesDiff = DEFAULT_LOGIN_LOOP_THRESHOLD_MINUTES } =
        options;
      const now = new Date();
      const nowIso = now.toISOString();

      if (lastLoginAttempt && userEmail) {
        const lastAttempt = new Date(lastLoginAttempt.timestamp);
        const timeDiff = now.getTime() - lastAttempt.getTime();
        const actualMinutesDiff = timeDiff / MILLISECONDS_PER_MINUTE;

        if (
          actualMinutesDiff < minutesDiff &&
          lastLoginAttempt.email === userEmail
        ) {
          logger.warn(
            'Login loop detected; clearing cache and retrying sign-in',
            {
              userEmail,
              lastLoginAttemptedAt: lastLoginAttempt.timestamp,
              currentAttemptAt: nowIso,
              minutesSinceLastAttempt: actualMinutesDiff,
              loginMethod: getLoginMethod(url),
            },
          );
          return clearCacheAndRedirect(url, userEmail, nowIso);
        }
      }

      if (userEmail) {
        setLastLoginAttempt({ timestamp: nowIso, email: userEmail });
      }

      window.location.assign(url);
    },
    [lastLoginAttempt, logger, clearCacheAndRedirect, setLastLoginAttempt],
  );

  const clearLoginLoopDetection = useCallback(() => {
    logger.info('Clearing login loop detection data after successful login');
    setLastLoginAttempt(null);
  }, [logger, setLastLoginAttempt]);

  return { redirectWithLogging, clearLoginLoopDetection };
};
